/**
 * The board-level activity log (`specs/M1-B8-activity-log.md` §2.3).
 *
 * Measured: a **700 × 730 panel at (740, 170)** titled `<board name> Log`, with three tabs
 * (`Activity` · `Last viewed` · `Updates`), a `Filter log` toolbar and rows on a 56 px pitch
 * `[ic§Board-level activity log]`. `Last viewed` has no known semantics (q260) and is rendered
 * disabled rather than invented.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityRows } from './ActivityRows';
import type { ActivityFacets, ActivityQuery, CardActivityRow, ItemCardSource } from './types';
import './item-card.css';

export type BoardLogTab = 'activity' | 'last-viewed' | 'updates';

export interface BoardActivityPanelProps {
  source: ItemCardSource;
  boardId: string;
  boardName: string;
  onClose(): void;
}

interface FilterState {
  time: string | null;
  group: string | null;
  column: string | null;
}

const EMPTY_FILTER: FilterState = { time: null, group: null, column: null };

/** monday's five buckets, in its order `[ic§Board-level activity log]`. */
function sinceFor(bucket: string | null): string | undefined {
  if (!bucket) return undefined;
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (bucket) {
    case 'today':
      return startOfDay.toISOString();
    case 'yesterday':
      return new Date(startOfDay.getTime() - 86_400_000).toISOString();
    case 'week':
      return new Date(startOfDay.getTime() - 6 * 86_400_000).toISOString();
    case 'month':
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    case 'year':
      return new Date(now.getFullYear(), 0, 1).toISOString();
    default:
      return undefined;
  }
}

export function BoardActivityPanel({ source, boardId, boardName, onClose }: BoardActivityPanelProps) {
  const [tab, setTab] = useState<BoardLogTab>('activity');
  const [rows, setRows] = useState<CardActivityRow[]>([]);
  const [facets, setFacets] = useState<ActivityFacets | null>(null);
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState('');

  const query = useMemo<ActivityQuery>(
    () => ({
      boardId,
      limit: 100,
      ...(filter.group ? { groupIds: [filter.group] } : {}),
      ...(filter.column ? { columnIds: [filter.column] } : {}),
      ...(sinceFor(filter.time) ? { from: sinceFor(filter.time) } : {}),
    }),
    [boardId, filter],
  );

  const reload = useCallback(async () => {
    // The panel is a URL segment: a failing source shows an empty log, it never unmounts the
    // board behind it (M0-F4 N2).
    const [list, counted] = await Promise.all([
      source.activity(query).catch(() => ({ rows: [], total: 0 })),
      source.facets(query).catch(() => null),
    ]);
    setRows(Array.isArray(list?.rows) ? list.rows : []);
    setFacets(counted);
  }, [source, query]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (filterOpen) setFilterOpen(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filterOpen, onClose]);

  const term = search.trim().toLowerCase();
  const visible =
    term === ''
      ? rows
      : rows.filter((row) =>
          `${row.rendered.entityName} ${row.rendered.label} ${row.rendered.after ?? ''}`
            .toLowerCase()
            .includes(term),
        );

  const facetColumn = (title: string, key: keyof FilterState, list: ActivityFacets[keyof ActivityFacets]) => (
    <div key={title}>
      <h3 className="ez-log-filter__column-title">{title}</h3>
      {(list as { key: string; label: string; count: number }[]).map((facet) => (
        <button
          key={facet.key}
          type="button"
          className={`ez-log-filter__facet${filter[key] === facet.key ? ' is-active' : ''}`}
          data-testid={`log-facet-${key}-${facet.key}`}
          onClick={() =>
            setFilter((current) => ({ ...current, [key]: current[key] === facet.key ? null : facet.key }))
          }
        >
          <span>{facet.label}</span>
          <span className="ez-log-filter__facet-count">{facet.count}</span>
        </button>
      ))}
    </div>
  );

  return (
    <aside className="ez-board-log" data-testid="board-activity-log" aria-label={`${boardName} Log`}>
      <header className="ez-board-log__header">
        <h1 className="ez-board-log__title" data-testid="board-log-title">{`${boardName} Log`}</h1>
        <button type="button" className="ez-board-log__close" aria-label="Close" data-testid="board-log-close" onClick={onClose}>
          ✕
        </button>
      </header>

      <nav className="ez-board-log__tabs" role="tablist">
        {(
          [
            ['activity', 'Activity', true],
            ['last-viewed', 'Last viewed', false],
            ['updates', 'Updates', false],
          ] as const
        ).map(([id, label, enabled]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            disabled={!enabled}
            title={enabled ? undefined : 'Not implemented yet (open questions q260, M7)'}
            className={`ez-board-log__tab${tab === id ? ' is-active' : ''}`}
            data-testid={`board-log-tab-${id}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="ez-board-log__toolbar">
        <button
          type="button"
          className="ez-board-log__filter"
          data-testid="board-log-filter"
          aria-expanded={filterOpen}
          onClick={() => setFilterOpen((open) => !open)}
        >
          Filter log
        </button>
        <input
          type="search"
          className="ez-board-log__search"
          aria-label="Search / Filter Board"
          placeholder="Search"
          data-testid="board-log-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {filterOpen && facets ? (
        <div className="ez-log-filter" data-testid="board-log-filter-panel">
          <div className="ez-log-filter__head">
            <span className="ez-log-filter__title">Filter log</span>
            <span className="ez-log-filter__count" data-testid="board-log-filter-count">
              {`Showing ${String(facets.total)} activities`}
            </span>
          </div>
          <div className="ez-log-filter__columns">
            {facetColumn('Time', 'time', facets.time)}
            {facetColumn('Group', 'group', facets.group)}
            {facetColumn('Column', 'column', facets.column)}
          </div>
        </div>
      ) : null}

      <div className="ez-board-log__body">
        <ActivityRows rows={visible} testId="board-activity-rows" />
      </div>
    </aside>
  );
}
